/**
 * The `sw_ui` cookie: the UI test arms this browser gets, set by proxy.ts on
 * every page response and read by UiTestProvider. Pure and browser-safe.
 *
 * Owner, 2026-10-25: "the client can't be slower bc of ui a/b testing". So the
 * arms are decided where the anonymous id already is (the server, which sets
 * that HttpOnly cookie on the same response) and travel with the page. The
 * browser asks nothing and waits for nothing.
 *
 * `<test>~<arm>~<run>~<phase>~<record>` per test, joined with `|`. Every part
 * is lowercase letters, digits and dashes (schemas/ui-tests.ts), so neither
 * separator can appear inside one.
 *
 * The value arrives ENCODED: Next's `response.cookies.set` writes `|` as
 * `%7C`. Read raw, the first test swallowed the rest into its record flag,
 * so on prod (2026-10-25, the first time two tests ran) home-default-view
 * served its arm but never counted anyone, and thumb-location was dropped,
 * so everyone saw control. `readCookie` decodes.
 */
import { ServedUiTestSchema, UiTestIdSchema, type ServedUiTest } from '../../schemas/ui-tests';

export const UI_ARMS_COOKIE = 'sw_ui';

/** Well under the 4 KB a cookie may hold; a value past it is dropped rather than truncated mid-test. */
const MAX_LENGTH = 1500;

export function encodeArms(tests: Record<string, ServedUiTest>): string {
  const value = Object.entries(tests)
    .map(([test, s]) => [test, s.arm, s.run, s.phase, s.record ? '1' : '0'].join('~'))
    .join('|');
  return value.length > MAX_LENGTH ? '' : value;
}

/** Whatever the cookie holds that parses; anything else is left out, which is control. */
export function decodeArms(value: string | null | undefined): Record<string, ServedUiTest> {
  const tests: Record<string, ServedUiTest> = {};
  if (!value) return tests;
  for (const part of value.split('|')) {
    const [test, arm, run, phase, record] = part.split('~');
    if (!UiTestIdSchema.safeParse(test).success) continue;
    // Anything but 0 or 1 is a value this did not write (the unread `%7C` was).
    if (record !== '0' && record !== '1') continue;
    const parsed = ServedUiTestSchema.safeParse({ arm, run, phase, record: record === '1' });
    if (parsed.success) tests[test] = parsed.data;
  }
  return tests;
}

/** One cookie's value out of a `document.cookie` string. */
export function readCookie(cookieString: string, name: string): string | null {
  for (const pair of cookieString.split(';')) {
    const at = pair.indexOf('=');
    if (at > 0 && pair.slice(0, at).trim() === name) return decodeValue(pair.slice(at + 1).trim());
  }
  return null;
}

/** A malformed escape is left as it came, and fails to parse as arms, which is control. */
function decodeValue(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}
